import { useState } from 'react';
import { useTags, Tag } from '@/hooks/use-tags';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Loader2, Plus, Tag as TagIcon, MoreHorizontal, Edit, Trash } from 'lucide-react';
import { Link } from 'wouter';

interface TagListProps {
  title?: string;
  showCreateButton?: boolean;
  filterTags?: string[]; // Optional array of tag names to filter
  onTagClick?: (tagName: string) => void;
  className?: string;
}

export function TagList({ 
  title = 'Tags', 
  showCreateButton = true,
  filterTags,
  onTagClick,
  className = ''
}: TagListProps) {
  const { tags, isLoading, createTag, updateTag, deleteTag, generateTagColor } = useTags();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  
  const filteredTags = filterTags 
    ? tags.filter(tag => filterTags.includes(tag.name))
    : tags;

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;
    
    createTag({
      name: newTagName.trim(),
      color: generateTagColor(),
    });
    
    setNewTagName('');
    setIsCreateDialogOpen(false);
  };

  const handleEditTag = () => {
    if (!editingTag || !newTagName.trim()) return;
    
    updateTag({
      id: editingTag.id,
      name: newTagName.trim(),
    });
    
    setNewTagName('');
    setEditingTag(null);
    setIsEditDialogOpen(false);
  };

  const startEditTag = (tag: Tag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setIsEditDialogOpen(true);
  };

  const handleDeleteTag = (tagId: number) => {
    if (confirm('Are you sure you want to delete this tag?')) {
      deleteTag(tagId);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{title}</h3>
        {showCreateButton && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" />
                New Tag
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a new tag</DialogTitle>
                <DialogDescription>
                  Enter a name for your new tag. Tags can be used to organize your content.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Input
                  placeholder="Tag name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTag} disabled={!newTagName.trim()}>
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredTags.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          <p>No tags found</p>
          {showCreateButton && (
            <p className="text-sm mt-1">Create a tag to get started</p>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {filteredTags.map((tag) => (
            <div key={tag.id} className="flex items-center">
              <Badge
                variant="outline"
                className="flex items-center gap-1 cursor-pointer"
                style={{ backgroundColor: tag.color || "#94a3b8", color: 'white' }}
                onClick={() => onTagClick ? onTagClick(tag.name) : null}
              >
                <TagIcon className="h-3 w-3" />
                {tag.name}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-1">
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link href={`/tags/${tag.id}`}>
                        View Tagged Content
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => startEditTag(tag)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteTag(tag.id)}
                      className="text-destructive"
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Badge>
            </div>
          ))}
        </div>
      )}
      
      {/* Edit Tag Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit tag</DialogTitle>
            <DialogDescription>
              Update the name for this tag.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTag} disabled={!newTagName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}